import React, { useState, useEffect } from 'react';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import VerifyEmail from './VerifyEmail';
import VerifyCode from './VerifyCode';
import { verifySession, isAuthenticated } from '../../services/auth';
import { AnalyticsEvents } from '../../services/analytics';

type AuthView = 'login' | 'register' | 'forgot' | 'reset' | 'verify' | 'verify-code';

interface AuthWrapperProps {
  onAuthSuccess: () => void;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ onAuthSuccess }) => {
  const [currentView, setCurrentView] = useState<AuthView>('login');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // CRÍTICO: Verificar si acaba de haber un logout (dentro de los últimos 2 segundos)
    // Si es así, ignorar todos los parámetros OAuth para evitar re-autenticación automática
    const logoutTimestamp = sessionStorage.getItem('logoutTimestamp');
    const justLoggedOut = logoutTimestamp && (Date.now() - parseInt(logoutTimestamp)) < 2000;

    if (justLoggedOut) {
      sessionStorage.removeItem('oauthParams');
      sessionStorage.removeItem('logoutTimestamp');
      // Continuar con verificación normal de sesión
      verifySession().then((result) => {
        if (result.success) {
          onAuthSuccess();
        } else {
          setCurrentView('login');
          setChecking(false);
        }
      });
      return;
    }

    // Primero, intentar obtener parámetros de sessionStorage (si fueron guardados antes de limpiar la URL)
    let token: string | null = null;
    let verify: string | null = null;
    let reset: string | null = null;
    let success: string | null = null;
    let error: string | null = null;

    const oauthParamsStr = sessionStorage.getItem('oauthParams');
    if (oauthParamsStr) {
      try {
        const oauthParams = JSON.parse(oauthParamsStr);
        token = oauthParams.token || null;
        verify = oauthParams.verify || null;
        reset = oauthParams.reset || null;
        success = oauthParams.success || null;
        error = oauthParams.error || null;
        // Limpiar sessionStorage después de leer
        sessionStorage.removeItem('oauthParams');
      } catch (e) {
      }
    }

    // Si no hay parámetros en sessionStorage, intentar leer de la URL (por si acaso)
    if (!success && !error && !token && !verify && !reset) {
      const urlParams = new URLSearchParams(window.location.search);
      token = urlParams.get('token');
      verify = urlParams.get('verify');
      reset = urlParams.get('reset');
      success = urlParams.get('success');
      error = urlParams.get('error');

      // Si hay parámetros en la URL, limpiarlos inmediatamente
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    // Google OAuth success: ?success=google_oauth&token=...
    // IMPORTANTE: Verificar esto PRIMERO antes de cualquier otra verificación de token
    // PERO solo procesar si NO hay una sesión activa (para evitar procesar después de logout)
    if (success === 'google_oauth' && !isAuthenticated()) {
      const oauthToken = token; // Ya tenemos el token de antes

      if (oauthToken) {
        // La URL ya fue limpiada arriba

        // Llamar al endpoint para establecer la sesión
        // En desarrollo, usar ruta relativa. En producción, usar la URL completa si está configurada
        const getAuthApiUrl = () => {
          // Si hay una URL configurada explícitamente, usarla
          if (process.env.REACT_APP_AUTH_URL) {
            return process.env.REACT_APP_AUTH_URL;
          }
          // Por defecto, usar ruta relativa (funciona si front y back están en el mismo dominio)
          return '/auth';
        };
        const AUTH_API_URL = getAuthApiUrl();
        fetch(`${AUTH_API_URL}/oauth-confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ token: oauthToken })
        })
        .then(response => response.json())
        .then((result) => {
          if (result.success && result.user) {
            // Guardar usuario en localStorage
            localStorage.setItem('user', JSON.stringify(result.user));
            // Track Google login exitoso
            AnalyticsEvents.userLogin('google');
            onAuthSuccess();
          } else {
            // Si falla, intentar verificar sesión normalmente
            verifySession().then((verifyResult) => {
              if (verifyResult.success) {
                onAuthSuccess();
              } else {
                setCurrentView('login');
                setChecking(false);
              }
            });
          }
        })
        .catch((error) => {
          // Intentar verificar sesión normalmente como fallback
          verifySession().then((verifyResult) => {
            if (verifyResult.success) {
              onAuthSuccess();
            } else {
              setCurrentView('login');
              setChecking(false);
            }
          });
        });
        return; // CRÍTICO: Salir inmediatamente
      } else {
        // Si no hay token, intentar verificar sesión normalmente
        // La URL ya fue limpiada arriba
        verifySession().then((result) => {
          if (result.success) {
            onAuthSuccess();
          } else {
            setCurrentView('login');
            setChecking(false);
          }
        });
        return; // CRÍTICO: Salir inmediatamente
      }
    }

    // Google OAuth error: ?error=oauth_error|oauth_failed|session_error|server_busy
    if (error && (error === 'oauth_error' || error === 'oauth_failed' || error === 'session_error' || error === 'server_busy')) {
      // La URL ya fue limpiada arriba
      setCurrentView('login');
      setChecking(false);
      // Si es error de servidor ocupado, mostrar mensaje específico
      if (error === 'server_busy') {
        alert('El servidor está recibiendo demasiadas solicitudes. Por favor, espera unos segundos e intenta ingresar nuevamente.');
      }
      return;
    }

    // Verificación de email: ?verify=email&token=...
    if (verify === 'email' && token) {
      setCurrentView('verify');
      setChecking(false);
      return;
    }

    // Reset de contraseña: ?reset=password&token=... o ?token=... (compatibilidad)
    // IMPORTANTE: Solo procesar como reset si NO es un success de OAuth
    // Excluir explícitamente si success es 'google_oauth'
    if (token && success !== 'google_oauth' && (reset === 'password' || (!verify && !success && success !== 'google_oauth'))) {
      setResetToken(token);
      setCurrentView('reset');
      setChecking(false);
      return;
    }

    // Si no hay parámetros especiales en la URL, verificar si ya hay una sesión activa
    const checkSession = async () => {
      if (isAuthenticated()) {
        try {
          const result = await verifySession();
          if (result.success) {
            onAuthSuccess();
            return;
          }
        } catch (error) {
        }
      }
      setChecking(false);
    };

    checkSession();
  }, [onAuthSuccess]);

  if (checking) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ fontFamily: "'Montserrat', sans-serif", color: '#64748b' }}>Verificando sesión...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {currentView === 'login' && (
        <Login
          onSuccess={onAuthSuccess}
          onSwitchToRegister={() => setCurrentView('register')}
          onForgotPassword={() => setCurrentView('forgot')}
          onVerifyCode={(email) => {
            setVerifyEmail(email);
            setCurrentView('verify-code');
          }}
        />
      )}

      {currentView === 'register' && (
        <Register
          onSuccess={() => setCurrentView('login')}
          onSwitchToLogin={() => setCurrentView('login')}
          onVerifyCode={(email) => {
            setVerifyEmail(email);
            setCurrentView('verify-code');
          }}
        />
      )}

      {currentView === 'forgot' && (
        <ForgotPassword
          onBack={() => setCurrentView('login')}
        />
      )}

      {currentView === 'reset' && resetToken && (
        <ResetPassword
          token={resetToken}
          onSuccess={() => setCurrentView('login')}
        />
      )}

      {currentView === 'verify' && (
        <VerifyEmail />
      )}

      {currentView === 'verify-code' && verifyEmail && (
        <VerifyCode
          email={verifyEmail}
          onSuccess={onAuthSuccess}
        />
      )}
    </>
  );
};

export default AuthWrapper;
